import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import measurementService from '../../../../services/measurementService';
import patientService from '../../../../services/patientService';
import { toast } from 'react-toastify';
import EmailModal from '../../../../components/EmailModal/EmailModal';
import { sendReportEmail } from '../../../../utils/reportEmailSend';
import { downloadBlob, printElement, toCsv } from '../../../../utils/reportExportUtils';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import MeasurementChartHeader from '../../ChartHeader/MeasurementChartHeader';
import { exportMeasurementTablesPdf } from '../../../../utils/measurementTablePdfExport';
import {
  DEFAULT_MEASUREMENT_QUERY,
  isFromToIncomplete,
  isMeasurementQueryDirty,
} from '../../../../utils/queryPeriodFilters';
import { formatPhoneForDisplay } from '../../../../utils/phoneUtils';

import DailyTrendChartWeight from './DailyTrendChartWeight';
import DailyTrendChartFat from './DailyTrendChartFat';
import DailyTrendChartBMI from './DailyTrendChartBMI';
import MeanChartWeight from './MeanChartWeight';
import MeanChartFat from './MeanChartFat';
import PieChartWeight from './PieChartWeight';
import PieChartFat from './PieChartFat';

import '../../MeasurementShell.css';
import './WeightGraphs.css';

const WeightGraphs = ({ practiceId: propPracticeId, patientId: propPatientId }) => {
    const params = useParams();
    const practiceId = propPracticeId || params.practiceId;
    const patientId = propPatientId || params.patientId;
    const location = useLocation();

    const [queryType, setQueryType] = useState(DEFAULT_MEASUREMENT_QUERY.queryType);
    const [dateRange, setDateRange] = useState(DEFAULT_MEASUREMENT_QUERY.dateRange);
    const [fromDate, setFromDate] = useState(DEFAULT_MEASUREMENT_QUERY.fromDate);
    const [toDate, setToDate] = useState(DEFAULT_MEASUREMENT_QUERY.toDate);

    const cacheKey = `chart_patient_meta_${practiceId}_${patientId}`;
    const [patientData, setPatientData] = useState(() => {
        try {
            const raw = sessionStorage.getItem(cacheKey);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [measurements, setMeasurements] = useState([]);
    const [scheduleTargets, setScheduleTargets] = useState(null);

    const [loading, setLoading] = useState(true);
    const [queryLoading, setQueryLoading] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const { updateBreadcrumbs } = useBreadcrumb();

    // Fetch patient data and schedule targets on mount
    useEffect(() => {
        if (practiceId && patientId) {
            setQueryType('dateRange');
            setDateRange('Last 30 days');
            setFromDate('');
            setToDate('');
            fetchPatientData();
            fetchScheduleTargets();
        }
    }, [practiceId, patientId]);

    // Fetch measurements on initial load / patient change only (filters apply via Query button).
    useEffect(() => {
        if (practiceId && patientId) {
            fetchMeasurements();
        }
    }, [practiceId, patientId]);

    // Breadcrumbs effect
    useEffect(() => {
        if (patientData?.patient && practiceId && patientId) {
            const fromDashboard = location.state?.fromDashboard;
            const rootCrumb = { label: 'Dashboard', path: '/dashboard' };
            const basePath = fromDashboard ? '/dashboard' : '/practices';

            updateBreadcrumbs([
                rootCrumb,
                { label: patientData.patient.practice_name || 'Practice', path: `${basePath}/${practiceId}`, state: { fromDashboard } },
                { label: 'Patients', path: `${basePath}/${practiceId}?tab=patient`, state: { fromDashboard } },
                { 
                  label: `${patientData.patient.last_name || ''}, ${patientData.patient.first_name || ''}`.replace(/^, | ,$|(, )+/g, '$1').trim() || 'Patient', 
                  path: `/practices/${practiceId}/patients/${patientId}`,
                  state: { fromDashboard }
                },
                { label: 'Weight Graphs', path: null, active: true }
            ]);
        }
    }, [patientData, practiceId, patientId, updateBreadcrumbs, location.state]);

    const fetchPatientData = async () => {
        try {
            const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
            if (response.data.success) {
                setPatientData(response.data.data);
                try {
                    sessionStorage.setItem(cacheKey, JSON.stringify(response.data.data));
                } catch {
                    // ignore cache write errors
                }
            }
        } catch (error) {
            console.error('Error fetching patient data:', error);
            toast.error('Failed to load patient data');
        }
    };

    const fetchScheduleTargets = async () => {
        try {
            const data = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
            setScheduleTargets(data);
        } catch (error) {
            console.error('Error fetching schedule targets:', error);
        }
    };

    const fetchMeasurements = async () => {
        try {
            setQueryLoading(true);
            let fromDateParam, toDateParam;
            const isAllHistory = queryType === 'dateRange' && String(dateRange).toUpperCase() === 'ALL';

            if (queryType === 'dateRange') {
                if (!isAllHistory) {
                    const days = parseInt(dateRange.replace(/\D/g, ''), 10) || 30;
                    const endDate = new Date();
                    const startDate = new Date();
                    startDate.setDate(startDate.getDate() - days);
                    fromDateParam = startDate.toISOString().split('T')[0];
                    toDateParam = endDate.toISOString().split('T')[0];
                }
            } else {
                // Inputs are usually YYYY-MM-DD from type="date"; also support legacy mm/dd/yyyy
                if (fromDate && toDate) {
                    if (fromDate.includes('/')) {
                        const [fromMonth, fromDay, fromYear] = fromDate.split('/');
                        const [toMonth, toDay, toYear] = toDate.split('/');
                        fromDateParam = `${fromYear}-${fromMonth.padStart(2, '0')}-${fromDay.padStart(2, '0')}`;
                        toDateParam = `${toYear}-${toMonth.padStart(2, '0')}-${toDay.padStart(2, '0')}`;
                    } else {
                        fromDateParam = fromDate;
                        toDateParam = toDate;
                    }
                }
            }

            if (isAllHistory || (fromDateParam && toDateParam)) {
                const response = await measurementService.getWeightByDateRange(
                    practiceId,
                    patientId,
                    isAllHistory ? undefined : fromDateParam,
                    isAllHistory ? undefined : toDateParam,
                    'date'
                );
                if (response.data?.success) {
                    setMeasurements(response.data.data || []);
                }
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching measurements:', error);
            if (error.response?.status !== 404) {
                toast.error('Failed to load measurements');
            }
            setMeasurements([]);
            setLoading(false);
        } finally {
            setQueryLoading(false);
        }
    };

    const handleQuery = () => {
        if (queryLoading) return;
        if (isFromToIncomplete(queryType, fromDate, toDate)) {
            toast.warning('Please select both start and end dates');
            return;
        }
        fetchMeasurements();
    };

    const handleReset = () => {
        setQueryType(DEFAULT_MEASUREMENT_QUERY.queryType);
        setDateRange(DEFAULT_MEASUREMENT_QUERY.dateRange);
        setFromDate(DEFAULT_MEASUREMENT_QUERY.fromDate);
        setToDate(DEFAULT_MEASUREMENT_QUERY.toDate);
    };

    const isResetDisabled = queryLoading || !isMeasurementQueryDirty({
        queryType, dateRange, fromDate, toDate, sortBy: DEFAULT_MEASUREMENT_QUERY.sortBy,
    });

    const patientName = patientData?.patient
        ? `${patientData.patient.first_name || ''} ${patientData.patient.last_name || ''}`.trim()
        : 'Loading...';
    const patientDOB = patientData?.patient?.date_of_birth
        ? new Date(patientData.patient.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        : 'Loading...';
    const patientVitals = patientData?.patient?.vitals || '';

    const exportBaseName = `Weight-Graphs-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}`;

    const handleExportPDF = async () => {
        if (measurements.length === 0) {
            toast.warning('No data to export');
            return;
        }
        try {
            const rows = measurements.map((m) => [
                m.measurement_date || m.measure_new_date_time?.split('T')[0] || m.created_at?.split('T')[0] || '',
                m.measurement_time || m.measure_new_date_time?.split('T')[1]?.slice(0, 5) || m.measure_date_time?.split(' ')[1]?.slice(0, 5) || '',
                m.weight == null ? '' : Number(m.weight).toFixed(1),
                (m.body_fat ?? m.fat) == null ? '' : Number(m.body_fat ?? m.fat).toFixed(1),
                m.bmi == null ? '' : Number(m.bmi).toFixed(1)
            ]);
            exportMeasurementTablesPdf({
                filename: `${exportBaseName}.pdf`,
                title: 'Weight Graphs Report',
                patient: { name: patientName, dob: patientDOB, phone: formatPhoneForDisplay(patientData?.patient?.phone_number), device: patientData?.device_serial || '' },
                tables: [
                    {
                        title: 'Weight Graph Readings',
                        head: [['Date', 'Time', 'Weight (kg)', 'Body Fat (%)', 'BMI']],
                        body: rows.length ? rows : [['No data available', '', '', '', '']]
                    }
                ]
            });
            toast.success('Report exported to PDF successfully');
        } catch (error) {
            console.error('Error exporting PDF:', error);
            toast.error('Failed to export PDF');
        }
    };

    const handleExportCsv = () => {
        if (measurements.length === 0) {
            toast.warning('No data to export');
            return;
        }
        const rows = [['Date', 'Time', 'Weight (kg)', 'Body Fat (%)', 'BMI']];
        measurements.forEach((m) => {
            rows.push([
                m.measurement_date || m.measure_new_date_time?.split('T')[0] || m.created_at?.split('T')[0] || '',
                m.measurement_time || m.measure_new_date_time?.split('T')[1]?.slice(0, 5) || m.measure_date_time?.split(' ')[1]?.slice(0, 5) || '',
                m.weight == null ? '' : Number(m.weight).toFixed(1),
                (m.body_fat ?? m.fat) == null ? '' : Number(m.body_fat ?? m.fat).toFixed(1),
                m.bmi == null ? '' : Number(m.bmi).toFixed(1)
            ]);
        });
        downloadBlob(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' }), `${exportBaseName}.csv`);
        toast.success('Report exported to CSV successfully');
    };

    const generateEmailContent = () => {
        if (!measurements || measurements.length === 0) return 'No weight measurement data available for the selected period.';
        const latest = measurements[measurements.length - 1]; // Assuming sorted
        return `Weight Graphs Report\nPatient: ${patientName} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Records: ${measurements.length}\n\nLatest Weight: ${latest?.weight != null ? latest.weight : 'N/A'}\nLatest Body Fat: ${latest?.body_fat || 'N/A'}%\nLatest BMI: ${latest?.bmi || 'N/A'}`;
    };

    const handlePrint = () => {
        try {
            const el = document.getElementById('wg-pdf-content');
            printElement(el, `Weight Graphs - ${patientName}`);
        } catch (e) {
            toast.error(e?.message || 'Print failed');
        }
    };

    return (
        <div className="weight-graphs-page">
            <MeasurementChartHeader
                practiceId={practiceId}
                patientId={patientId}
                vitals={patientVitals}
                activeVital="weight"
                patientName={patientName}
                patientDOB={patientDOB}
                queryType={queryType}
                setQueryType={setQueryType}
                dateRange={dateRange}
                setDateRange={setDateRange}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                onQuery={handleQuery}
                onReset={handleReset}
                queryDisabled={queryLoading}
                resetDisabled={isResetDisabled}
                onPrint={handlePrint}
                onExportPdf={handleExportPDF}
                onExportCsv={handleExportCsv}
                onEmail={() => setShowEmailModal(true)}
                hasData={measurements.length > 0}
            />

            {/* Graph Renderings */}
            <div className="wg-charts-container" id="wg-pdf-content">
                {loading || queryLoading ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
                ) : (
                    <>
                        <div className="bp-section wg-chart-section">
                            <div className="bp-section-title-row">
                                <h2 className="bp-section-title">Standard Day Chart of Weight</h2>
                            </div>
                            <div className="bp-card"><div className="bp-card-body">
                                <DailyTrendChartWeight measurements={measurements} />
                            </div></div>
                        </div>
                        <div className="bp-section wg-chart-section">
                            <div className="bp-section-title-row">
                                <h2 className="bp-section-title">Standard Day Chart of Fat</h2>
                            </div>
                            <div className="bp-card"><div className="bp-card-body">
                                <DailyTrendChartFat measurements={measurements} />
                            </div></div>
                        </div>
                        <div className="bp-section wg-chart-section">
                            <div className="bp-section-title-row">
                                <h2 className="bp-section-title">Standard Day Chart of BMI</h2>
                            </div>
                            <div className="bp-card"><div className="bp-card-body">
                                <DailyTrendChartBMI measurements={measurements} />
                            </div></div>
                        </div>

                        <div className="bp-section wg-mean-chart-wrapper">
                            <div className="bp-section-title-row">
                                <h2 className="bp-section-title">Mean Chart of Weight</h2>
                            </div>
                            <div className="bp-card"><div className="bp-card-body">
                                <MeanChartWeight measurements={measurements} scheduleTargets={scheduleTargets} />
                            </div></div>
                        </div>
                        <div className="bp-section wg-mean-chart-wrapper">
                            <div className="bp-section-title-row">
                                <h2 className="bp-section-title">Mean Chart of Fat</h2>
                            </div>
                            <div className="bp-card"><div className="bp-card-body">
                                <MeanChartFat measurements={measurements} scheduleTargets={scheduleTargets} />
                            </div></div>
                        </div>

                        <div className="bp-section wg-pie-chart-wrapper">
                            <div className="bp-section-title-row">
                                <h2 className="bp-section-title">Pie Chart of Weight</h2>
                            </div>
                            <div className="bp-card"><div className="bp-card-body">
                                <PieChartWeight measurements={measurements} scheduleTargets={scheduleTargets} />
                            </div></div>
                        </div>
                        <div className="bp-section wg-pie-chart-wrapper">
                            <div className="bp-section-title-row">
                                <h2 className="bp-section-title">Pie Chart of Fat</h2>
                            </div>
                            <div className="bp-card"><div className="bp-card-body">
                                <PieChartFat measurements={measurements} scheduleTargets={scheduleTargets} />
                            </div></div>
                        </div>
                    </>
                )}
            </div>

            {/* Modals */}
            <EmailModal
                isOpen={showEmailModal}
                onClose={() => setShowEmailModal(false)}
                onSend={async (emailData) => {
                    try {
                        const res = await sendReportEmail(emailData, { event_id: 11 });
                        if (!res.success) throw new Error(res.error || res.message);
                        toast.success('Email queued successfully');
                        setShowEmailModal(false);
                    } catch (e) {
                        toast.error(e?.message || 'Failed to send email');
                    }
                }}
                defaultSubject={`Weight Graphs Report - ${new Date().toLocaleDateString()}`}
                defaultContent={generateEmailContent()}
            />
        </div>
    );
};

export default WeightGraphs;
